/**
 * Feature flags for enabling/disabling application modules and functionality
 *
 * These flags control which navigation menu items, pages, and features are available.
 * Flags are checked in navbar.component.ts hideLinks() method and throughout the app.
 *
 * Priority: Feature flags are applied BEFORE role-based filtering.
 * If a feature is disabled, it won't appear even for users with appropriate roles.
 *
 * @see navbar.component.ts hideLinks() method
 * @see FeaturesService
 */
export interface FeaturesModel {
    // ==================== Navigation & Module Visibility ====================

    /**
     * Enables the Appeals module and all appeal-related navigation items
     * When false: Hides entire Appeal menu (8 items) AND /administration/appeal from Organization menu
     * Affects:
     *   - /administration/appeal-register
     *   - /administration/my-appeals-paralegal
     *   - /administration/sent-appeal
     *   - /administration/sent-to-organization
     *   - /administration/my-organizations
     *   - /administration/assigned-to-me
     *   - /administration/my-appeals
     *   - /administration/client-appeals
     *   - /administration/appeal (in Organization menu)
     * @default true
     */
    enableAppeals: boolean;

    /**
     * Enables the Surveys/Polls module
     * When false: Hides Polls menu item
     * Affects: /administration/polls
     * @default true
     */
    enableSurveys: boolean;

    /**
     * Enables the Violation Directories module (areas, groups, types, location types)
     * When false: Hides entire Violations menu (4 items)
     * Affects: /violation/sphere, /violation/groups, /violation/kinds, /violation/location-types
     * @default true
     */
    enableViolationDirectories: boolean;

    /**
     * Enables Paralegal users and related administration
     * When false: Hides paralegal menu item from Administration menu
     * Affects: /administration/paralegals
     * @default true
     */
    enableParalegals: boolean;

    /**
     * Enables Organization Employees management
     * When false: Hides organization employees menu item from Administration menu
     * Affects: /administration/organization-employees
     * @default true
     */
    enableOrganizationEmployees: boolean;

    /**
     * Enables the Name Dictionary module for custom name/title management
     * When false: Hides entire Dictionaries menu
     * Affects: /administration/dictionary-name
     * @default true
     */
    enableNameDictionary: boolean;

    /**
     * Enables Name Dictionary validation in backend CheckNames() method
     * When true: Backend validates first names and middle names against NamesDictionary database
     * When false: Backend skips dictionary validation (for deployments without name dictionary data)
     * NOTE: Can only be true if enableNameDictionary is true
     * @default true
     */
    enableNameDictionaryValidation: boolean;

    /**
     * Enables Partners management in the Clients menu, Partners tab on client details page, and Partners report
     * When false: Filters out partners items from menus AND hides Partners tab in client dialog
     * Affects:
     *   - /administration/partners (Clients menu item)
     *   - Partners tab in /administration/clients-work client details dialog
     *   - /report/partners-report (Reports menu item "Звіт Партнери")
     * @default true
     */
    enablePartners: boolean;

    /**
     * Enables Contacts management in the Clients menu
     * When false: Filters out contacts item from Clients menu
     * Affects: /administration/contacts
     * @default true
     */
    enableContacts: boolean;

    /**
     * Enables representative verification functionality
     * Controls whether representative verification features are available
     * @default true
     */
    enableRepresentativeVerification: boolean;

    /**
     * Enables Individual Number field/functionality
     * Controls whether individual number tracking is enabled in the system
     * @default true
     */
    enableIndividualNumber: boolean;
    showPactReports: boolean;
    showMergeClientsAction: boolean;
    useUicForAidsOrgCode: boolean;
    showUicInClientsRegister: boolean;
    showMiddleNameInClientsRegister: boolean;
    showSelfServiceControls: boolean;
    organizationTerritorialCommunityLabel: string;

    /**
     * When true, Settlement is required on the Organization card for Service Delivery Point role (UA).
     * When false, Settlement is optional for all roles (KZ).
     * @default true
     */
    organizationSettlementRequired: boolean;

    /**
     * List of enabled organization role IDs
     * Controls which organization roles are available in the system
     * Role IDs correspond to OrgRoles enum values
     * @example [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] for all roles
     * @default [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]
     */
    enabledOrganizationRoles: number[];

    // ==================== Business Validation Rules ====================

    /**
     * Enforces Cyrillic characters in name format validation
     * When true: Names must use Cyrillic alphabet (Ukrainian/Russian)
     * When false: Allows any characters in names
     * Useful for different regions with different alphabet requirements
     * @default true for UA/RU deployments, false for KZ deployment
     */
    enableCyrillicCharactersNameFormatValidation: boolean;

    /**
     * Regular expression pattern for phone number validation
     * Used to validate phone number input fields throughout the application
     * @example "^\\+380\\d{9}$" for Ukraine (+380 followed by 9 digits)
     * @example "^\\+7\\d{10}$" for Kazakhstan/Russia (+7 followed by 10 digits)
     * @default "^\\+380\\d{9}$"
     */
    phoneNumberPattern: string;

    /**
     * Country calling code prefix (without +)
     * Used for phone number formatting and validation
     * @example "380" for Ukraine
     * @example "7" for Kazakhstan/Russia
     * @default "380"
     */
    phoneCountryCode: string;

    /**
     * Input mask for phone number fields (ngx-mask format)
     * Used for formatting phone number input (applied after the prefix)
     * @example "(000) 000-0000" for formatted display
     * @default "(000) 000-0000"
     */
    phoneMask: string;
}

/**
 * Default feature configuration for Ukraine deployment
 *
 * All features enabled by default.
 * Phone validation set for Ukraine (+380 format).
 * Cyrillic name validation enabled.
 *
 * Country-specific deployments override these values via /assets/app.config.json
 */
export const DEFAULT_FEATURES: FeaturesModel = {
    enableAppeals: true,
    enableSurveys: true,
    enableViolationDirectories: true,
    enableParalegals: true,
    enableOrganizationEmployees: true,
    enableNameDictionary: true,
    enableNameDictionaryValidation: true,
    enablePartners: true,
    enableContacts: true,
    enableRepresentativeVerification: true,
    enableIndividualNumber: true,
    showPactReports: true,
    showMergeClientsAction: true,
    useUicForAidsOrgCode: false,
    showUicInClientsRegister: false,
    showMiddleNameInClientsRegister: true,
    showSelfServiceControls: true,
    organizationTerritorialCommunityLabel: 'organization.territorialCommunity',
    organizationSettlementRequired: true,
    enabledOrganizationRoles: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
    enableCyrillicCharactersNameFormatValidation: true,
    phoneNumberPattern: '^0\\d{9}$',
    phoneCountryCode: '380',
    phoneMask: '(000) 000-0000'
};
